import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Loader2, Globe2, Plus } from "lucide-react";
import { frApi, errMsg, fieldCls, labelCls, TIER_CLS } from "./frUtils";

const MATCH = { exact: "Recognised", alias: "Recognised (alternate name)", fuzzy: "Closest match by spelling. Please check", default: "Not in the city list: Tier 4 by default" };

/** City / Country field: recognises Indian cities (with tier) and foreign countries; missing ones can be saved permanently. */
export default function CityField({ value, onChange, mode = "online_india", onSwitchMode }) {
    const [hit, setHit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tier, setTier] = useState(4);
    const [seq, setSeq] = useState(0);
    useEffect(() => {
        if (!value.trim()) { setHit(null); return undefined; }
        setLoading(true);
        const t = setTimeout(() => {
            frApi.get("/resolve-location", { q: value, mode }).then(setHit).catch(() => setHit(null)).finally(() => setLoading(false));
        }, 350);
        return () => clearTimeout(t);
    }, [value, mode, seq]);
    const save = async () => {
        try {
            if (mode === "foreign") await frApi.post("/countries", { name: value.trim() });
            else await frApi.post("/cities", { name: value.trim(), tier: Number(tier) });
            toast.success(`${value.trim()} saved. It will be recognised automatically from now on.`);
            setSeq((n) => n + 1);
        } catch (e) { toast.error(errMsg(e)); }
    };
    const c = hit?.city;
    const unknownCity = mode !== "foreign" && c && c.match === "default";
    const unknownCountry = mode === "foreign" && hit && !hit.country;
    return (
        <div>
            <label className={labelCls} htmlFor="fr-city">{mode === "foreign" ? "Country" : mode === "home" ? "City (Home Tuition)" : "City / Country"}</label>
            <div className="relative">
                {mode === "foreign" ? <Globe2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /> : <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />}
                <input id="fr-city" value={value} onChange={(e) => onChange(e.target.value)} data-testid="fr-city-input" autoComplete="off" className={`${fieldCls} pl-9`}
                    placeholder={mode === "foreign" ? "e.g. UAE, United Kingdom, USA" : mode === "home" ? "Jabalpur, Bhopal or Nagpur" : "e.g. Bengaluru, Datia"} />
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {hit && hit.kind !== "none" && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs" data-testid="fr-city-match">
                    {hit.country ? (
                        <><span className="px-2 py-0.5 rounded-full border font-bold bg-violet-100 text-violet-800 border-violet-200" data-testid="fr-city-tier">Foreign / NRI</span>
                            <span className="font-semibold text-slate-800" data-testid="fr-city-name">{hit.country.name}</span>
                            {mode !== "foreign" && onSwitchMode && <button type="button" onClick={() => onSwitchMode("foreign")} className="text-amber-800 font-semibold underline underline-offset-2" data-testid="fr-switch-foreign">Switch to Live Online – Foreign/NRI</button>}</>
                    ) : c && (
                        <><span className={`px-2 py-0.5 rounded-full border font-bold ${TIER_CLS[c.tier]}`} data-testid="fr-city-tier">Tier {c.tier}</span>
                            {c.city && <span className="font-semibold text-slate-800" data-testid="fr-city-name">{c.city}</span>}
                            <span className={c.match === "fuzzy" ? "text-amber-700 font-semibold" : "text-slate-500"}>{MATCH[c.match]}</span>
                            {mode === "home" && <span className={hit.home_ok ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"} data-testid="fr-home-available">{hit.home_ok ? "Home Tuition available" : "Home Tuition not available here"}</span>}</>
                    )}
                </div>
            )}
            {(unknownCity || unknownCountry) && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="fr-location-add">
                    {unknownCity && <select value={tier} onChange={(e) => setTier(e.target.value)} className="h-9 px-2 text-sm border border-slate-200 rounded-lg" data-testid="fr-location-add-tier">
                        {[1, 2, 3, 4].map((t) => <option key={t} value={t}>Tier {t}</option>)}</select>}
                    <button type="button" onClick={save} className="h-9 px-3 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] text-xs font-semibold inline-flex items-center gap-1" data-testid="fr-location-add-btn">
                        <Plus className="w-3.5 h-3.5" /> Save "{value.trim()}" as a new {unknownCountry ? "country" : "city"}
                    </button>
                </div>
            )}
        </div>
    );
}
